import { useState } from 'react'
import { Archive, Download, Loader2 } from 'lucide-react'
import { api } from '../../api/client'
import { PageHeader } from '../../components/PageHeader'
import { useI18n } from '../../i18n'

export function BackupPage() {
  const { t } = useI18n()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [lastBackup, setLastBackup] = useState('')

  async function startBackup() {
    setLoading(true)
    setError('')
    try {
      const token = localStorage.getItem('swiss_mail_token')
      const res = await fetch(`${import.meta.env.VITE_API_URL ?? '/api/v1'}/admin/backup`, {
        headers: { Authorization: `Bearer ${token}` },
        credentials: 'include',
      })
      if (!res.ok) {
        const data = await res.json().catch(() => null) as { error?: string } | null
        throw new Error(data?.error ?? `Backup failed: ${res.status}`)
      }
      const blob = await res.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      const disposition = res.headers.get('Content-Disposition')
      const filename = disposition?.match(/filename="?([^"]+)"?/)?.[1] ?? `swiss-mail-backup-${Date.now()}.tar.gz`
      a.href = url
      a.download = filename
      a.click()
      URL.revokeObjectURL(url)
      setLastBackup(new Date().toLocaleString())
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Backup failed')
    } finally {
      setLoading(false)
    }
  }

  return <>
    <PageHeader title="admin.backup" />

    <div className="max-w-2xl space-y-6">
      <section className="panel p-6">
        <div className="flex items-start gap-4">
          <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary shrink-0">
            <Archive size={24} />
          </span>
          <div>
            <h2 className="text-lg font-semibold">{t('admin.backupTitle')}</h2>
            <p className="mt-1 text-sm text-muted-foreground">{t('admin.backupDesc')}</p>
          </div>
        </div>

        <div className="mt-6 rounded-lg bg-muted/30 p-4 text-sm space-y-1.5">
          <p className="font-medium">{t('admin.backupIncludes')}</p>
          <ul className="list-disc list-inside text-muted-foreground space-y-1">
            <li>{t('admin.backupItemDb')}</li>
            <li>{t('admin.backupItemSource')}</li>
            <li>{t('admin.backupItemConfig')}</li>
            <li>{t('admin.backupItemGuide')}</li>
          </ul>
        </div>

        {error && <p className="mt-4 rounded-lg bg-destructive/10 px-4 py-2.5 text-sm text-destructive">{error}</p>}

        <div className="mt-6 flex items-center gap-4">
          <button onClick={startBackup} disabled={loading} className="btn-primary flex items-center gap-2 px-6 py-2.5 disabled:opacity-50">
            {loading ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
            {loading ? t('admin.backupGenerating') : t('admin.backupDownload')}
          </button>
          {lastBackup && <span className="text-xs text-muted-foreground">{t('admin.backupLast')}: {lastBackup}</span>}
        </div>
      </section>
    </div>
  </>
}
